import { useQueryClient } from "@tanstack/react-query";
import Button from "components/buttons/Button";
import { t } from "i18next";
import type React from "react";
import { useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Headline from "~/components/Headline/Headline";
import Hint from "~/components/Hint";
import Modal from "~/components/Modal";
import Paragraph from "~/components/Paragraph";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { createMonthlyClosing } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingApiDispatchers.ts";
import { USERS_MONTHLY_CLOSINGS_QUERY_KEY } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

type CreateMonthlyClosingModalProps = {
	isOpen: boolean;
	month: string;
	onClose: () => void;
	userId: string;
	year: string;
};

const CreateMonthlyClosingModal: React.FunctionComponent<CreateMonthlyClosingModalProps> = ({
	isOpen,
	onClose,
	month,
	userId,
	year,
}) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	const queryClient = useQueryClient();

	const handleConfirmationClick = async () => {
		try {
			setBusy(true);
			const date = formatDateToYYYYMMDD(new Date(parseInt(year), parseInt(month), 1));

			await createMonthlyClosing({ userId, month: date });

			await queryClient.invalidateQueries({ queryKey: USERS_MONTHLY_CLOSINGS_QUERY_KEY(userId) });
			onClose();
		} catch (error) {
			const errorData = handleApiError(error);
			if (errorData.type === ApiResponseErrorType.VALIDATION) {
				if (
					errorData.messages.month.find(
						(validationError) => validationError.rule === "monthlyClosingNotNotExistsRule",
					)
				) {
					onClose();
				}
			} else {
				setError("Ein unbekannter Fehler ist aufgetreten.");
			}
		}
		setBusy(false);
	};

	return (
		<Modal open={isOpen}
			   setOpen={onClose}>
			<div className="relative flex flex-col gap-3">
				{busy && <SidebarBusyOverlay/> }
				<Headline type="h4">Monat schließen</Headline>
				<div>
					{!error ? (
						<Hint theme="info">
							<Paragraph>Bitte überprüfe alle Erfassungen auf Richtigkeit, bevor Du den Monat
								schließt.</Paragraph>
						</Hint>
					) : (
						<Hint theme="error">{error}</Hint>
					)}
				</div>
				<div className="mt-4">
					<Button disabled={busy || !!error}
							onClick={handleConfirmationClick}>
						{t(normalizeTKey("form:buttonTexts.save"))}
					</Button>
					<Button theme="none"
							onClick={onClose}>
						{t(normalizeTKey("form:buttonTexts.cancel"))}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default CreateMonthlyClosingModal;
